import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCrmTenant } from '../crm/useCrmTenant';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  CircularProgress,
} from '@mui/material';
import { paymentApi } from '../../services';
import type { GatewayPlan, PaymentPlanOption, PaymentProvider } from '../../services/api/paymentApi';
import {
  RECURRING_PROVIDERS,
  formatOptionCadence,
  isOneTimePlan,
  formatPlanCadence,
  formatPlanMoney,
} from '../../services/api/paymentApi';
import { API_BASE_URL } from '../../config/api';
import {
  openCashfreeCheckout,
  openRazorpayCheckout,
  startCashfreeSubscription,
  REDIRECT_PROVIDERS,
} from '../../utils/paymentProviders';
import {
  computePaymentBreakdown,
  computePlanBreakdowns,
  DEFAULT_GST_RATE,
  DiscountMode,
  GstMode,
  PaymentBreakdown,
} from '../../utils/paymentCalc';
import {
  CompletedPaymentDetails,
  PaymentFieldStatus,
  PaymentSubmissionContext,
} from '../form-renderer/PaymentSubmissionContext';
import CustomButton from './Button';
import PlanDetails from './PlanDetails';

interface PaymentSectionData {
  content?: React.ReactNode;
  field_name?: string;
  data?: {
    className?: string;
    amount?: number | string;
    description?: string;
    discountMode?: DiscountMode;
    discountValue?: number | string;
    gstMode?: GstMode;
    breakdownDisplay?: 'off' | 'inline' | 'dialog';
    field_name?: string;
    isRequired?: boolean;
    type?: string;
    onSubmitCallback?: (() => void) | string;
    href?: string;
    openInNewTab?: boolean | string;
    /** Providers the visitor may pay with; the first is the default. */
    providers?: PaymentProvider[];
    paymentMode?: 'one_time' | 'plan';
    gstRate?: number | string;
    /** Plans the visitor chooses from in plan mode, each with a plan id per provider. */
    planOptions?: PaymentPlanOption[];
    preparePayment?: () => Promise<{
      leadId: string;
      formId: string;
      name: string;
      email: string;
      phone: string;
    }>;
    [key: string]: any;
  };
}

interface PaymentSectionProps {
  data: PaymentSectionData;
}

const PROVIDER_LABELS: Record<PaymentProvider, string> = {
  cashfree: 'Cashfree',
  razorpay: 'Razorpay',
  hdfc: 'HDFC',
};

const formatCurrency = (value: number, currency: string = 'INR'): string =>
  formatPlanMoney(value, currency);

const BreakdownRows: React.FC<{
  breakdown: PaymentBreakdown;
  discountMode?: DiscountMode;
  gstMode?: GstMode;
  gstRate?: number;
  currency?: string;
}> = ({ breakdown, discountMode, gstMode, gstRate = DEFAULT_GST_RATE, currency }) => (
  <div style={{ fontSize: '0.9rem' }}>
    <Row label="Amount" value={formatCurrency(breakdown.baseAmount, currency)} />
    {discountMode && discountMode !== 'none' && breakdown.discountAmount > 0 && (
      <Row
        label="Discount"
        value={`- ${formatCurrency(breakdown.discountAmount, currency)}`}
      />
    )}
    {gstMode && gstMode !== 'none' && (
      <Row label={`GST (${gstRate}%)${gstMode === 'included' ? ' (Included)' : ''}`} value={formatCurrency(breakdown.gst, currency)} />
    )}
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid #e0e0e0',
        marginTop: 6,
        paddingTop: 6,
        fontWeight: 600,
      }}
    >
      <span>Total</span>
      <span>{formatCurrency(breakdown.total, currency)}</span>
    </div>
  </div>
);

const Row: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '2px 0',
      color: '#555',
    }}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const PAID_STATUSES = new Set([
  'paid', 'success', 'captured', 'charged', 'active', 'authenticated', 'completed',
]);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls the gateway's own record until it reports the payment as settled. */
const verifyPaid = async (
  tenantId: string,
  paymentId: string,
  provider: string,
): Promise<boolean> => {
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      const result = await paymentApi.getPaymentRecord(tenantId, paymentId, provider, true);
      const status = String((result?.data || result)?.status || '').toLowerCase();
      if (PAID_STATUSES.has(status)) return true;
    } catch {
      // transient lookup failure - retry
    }
    await sleep(1500);
  }
  return false;
};

const PROVIDER_COLORS: Record<PaymentProvider, string> = {
  cashfree: '#6c2bd9',
  razorpay: '#2b6cdf',
  hdfc: '#0b4f9c',
};

const StepHeading: React.FC<{
  index: number;
  title: string;
  done?: boolean;
  hint?: string;
}> = ({ index, title, done, hint }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        background: done ? '#2e7d32' : '#1976d2',
      }}
    >
      {done ? '✓' : index}
    </div>
    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1b1f24' }}>{title}</span>
    {hint && <span style={{ fontSize: '0.8rem', color: '#8a929c' }}>· {hint}</span>}
  </div>
);

const ProviderTile: React.FC<{
  provider: PaymentProvider;
  active: boolean;
  disabled?: boolean;
  onSelect: () => void;
}> = ({ provider, active, disabled, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    disabled={disabled}
    onClick={onSelect}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 12,
      cursor: disabled ? 'default' : 'pointer',
      textAlign: 'left',
      background: active ? '#f2f8ff' : '#fff',
      border: `2px solid ${active ? '#1976d2' : '#e3e6ea'}`,
      boxShadow: active ? '0 4px 12px rgba(25,118,210,0.15)' : '0 1px 2px rgba(0,0,0,0.05)',
      transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    }}
  >
    <div
      aria-hidden
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        color: '#fff',
        background: PROVIDER_COLORS[provider],
      }}
    >
      {PROVIDER_LABELS[provider].charAt(0)}
    </div>
    <span style={{ flex: 1, fontWeight: 600, color: '#1b1f24' }}>{PROVIDER_LABELS[provider]}</span>
    <span
      aria-hidden
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        boxSizing: 'border-box',
        border: `2px solid ${active ? '#1976d2' : '#c4c9d0'}`,
        background: active ? 'radial-gradient(#1976d2 45%, #fff 50%)' : '#fff',
      }}
    />
  </button>
);

const PlanCards: React.FC<{
  plans: PaymentPlanOption[];
  selectedId: string;
  disabled?: boolean;
  onSelect: (id: string) => void;
  infoFor: (plan: PaymentPlanOption) => GatewayPlan | undefined;
  gstRate: number;
  currency: string;
}> = ({ plans, selectedId, disabled, onSelect, infoFor, gstRate, currency }) => (
  <div
    role="radiogroup"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: 14,
      marginBottom: 14,
    }}
  >
    {plans.map((plan) => {
      const active = plan.id === selectedId;
      const info = infoFor(plan);
      const description = plan.description || info?.description;
      return (
        <button
          key={plan.id}
          type="button"
          role="radio"
          aria-checked={active}
          disabled={disabled}
          onClick={() => onSelect(plan.id)}
          style={{
            position: 'relative',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minHeight: 120,
            padding: '16px 16px 14px',
            borderRadius: 16,
            cursor: disabled ? 'default' : 'pointer',
            background: active ? 'linear-gradient(160deg, #f2f8ff 0%, #ffffff 70%)' : '#fff',
            border: `2px solid ${active ? '#1976d2' : '#e3e6ea'}`,
            boxShadow: active
              ? '0 6px 18px rgba(25,118,210,0.18)'
              : '0 1px 3px rgba(0,0,0,0.06)',
            transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 20,
              height: 20,
              borderRadius: '50%',
              boxSizing: 'border-box',
              border: `2px solid ${active ? '#1976d2' : '#c4c9d0'}`,
              background: active ? '#1976d2' : '#fff',
              color: '#fff',
              fontSize: 12,
              lineHeight: '16px',
              textAlign: 'center',
            }}
          >
            {active ? '✓' : ''}
          </span>
          <span style={{ fontWeight: 700, fontSize: '1rem', paddingRight: 28, color: '#1b1f24' }}>
            {plan.label || info?.name || 'Plan'}
          </span>
          {description && (
            <span style={{ fontSize: '0.82rem', color: '#5b6470', lineHeight: 1.4 }}>
              {description}
            </span>
          )}
          {plan.isPopular && (
            <span
              style={{
                alignSelf: 'flex-start',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#0d47a1',
                background: '#e8f1fc',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              Most popular
            </span>
          )}
          {plan.price !== undefined && plan.price !== '' && (() => {
            const { first, recurring: perCycle } = computePlanBreakdowns(plan, gstRate);
            const recurring = isOneTimePlan(plan) ? first : perCycle;
            const hasDiscount =
              plan.discountMode && plan.discountMode !== 'none' && recurring.discountAmount > 0;
            const strike = hasDiscount
              ? computePaymentBreakdown(Number(plan.price), 'none', 0, plan.gstMode || 'none', gstRate).total
              : null;
            return (
              <div style={{ marginTop: 'auto', paddingTop: 6 }}>
                {strike !== null && (
                  <div style={{ fontSize: '0.85rem', color: '#8a929c', textDecoration: 'line-through', marginRight: 6 }}>
                    {formatPlanMoney(strike, currency)}
                  </div>
                )}
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1b1f24' }}>
                  {formatPlanMoney(recurring.total, currency)}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7380' }}>{`Per ${formatOptionCadence(plan)}`}</div>
              </div>
            );
          })()}
          {!(plan.price !== undefined && plan.price !== '') && info?.amount !== undefined && (
            <div style={{ marginTop: 'auto', paddingTop: 6, }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1b1f24' }}>
                {formatPlanMoney(info.amount, info.currency)}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7380' }}>
                Per {formatPlanCadence(info)}
              </div>
            </div>
          )}
        </button>
      );
    })}
  </div>
);

const PaymentSection = React.forwardRef<
  HTMLButtonElement,
  PaymentSectionProps
>((props, ref) => {
  const { tenantId } = useParams<{ tenantId?: string }>();
  const crmTenantId = useCrmTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, content } = props.data;
  const paymentSubmission = useContext(PaymentSubmissionContext);

  const fieldName = data?.field_name || props.data.field_name || 'payment';
  const storageKey = `formPayment:${tenantId || 'unknown'}:${fieldName}:done`;

  // PaymentSection can be unmounted/remounted mid-flight (e.g. the parent
  // ReactFormGenerator remounts fields when formValues changes), so status is
  // lifted into FormRenderer via context instead of living purely in local
  // state - local state is only the fallback for builder-preview mode, where
  // no PaymentSubmissionContext is provided.
  const [localStatus, setLocalStatus] = useState<PaymentFieldStatus>('idle');
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const externalStatus = paymentSubmission?.getPaymentStatus?.(fieldName);
  const status = externalStatus?.status ?? localStatus;
  const errorMessage = externalStatus?.errorMessage ?? localErrorMessage;

  const updateStatus = (next: PaymentFieldStatus, message: string = '') => {
    setLocalStatus(next);
    setLocalErrorMessage(message);
    paymentSubmission?.setPaymentStatus?.(fieldName, {
      status: next,
      errorMessage: message,
    });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const actionButtonRef = useRef<HTMLButtonElement>(null);

  // Plan / provider selection ------------------------------------------------
  // Plan first, then provider: only providers that offer the chosen plan show.
  const isPlanMode = data?.paymentMode === 'plan';
  const defaultProviders: PaymentProvider[] = ['cashfree'];
  const configuredProviders: PaymentProvider[] = (
    data?.providers?.length ? data.providers : defaultProviders
  );
  const enabledProviders: PaymentProvider[] = configuredProviders.length
    ? configuredProviders
    : defaultProviders;

  const currency = 'INR';
  const gstRate =
    data?.gstRate === undefined || data?.gstRate === '' || !Number.isFinite(Number(data.gstRate))
      ? DEFAULT_GST_RATE
      : Number(data.gstRate);
  const planOptions = (data?.planOptions || []).filter((plan) => plan.label || plan.id);
  const isManaged = (plan?: PaymentPlanOption) =>
    plan?.price !== undefined && plan.price !== '';
  const [selectedPlanKey, setSelectedPlanKey] = useState('');
  const selectedPlan =
    planOptions.find((plan) => plan.id === selectedPlanKey) ||
    (planOptions.length === 1 ? planOptions[0] : undefined);

  // One-time plans are a single charge on any provider; recurring plans need a
  // gateway plan, so only providers that have one are offered.
  const isOneTimeSelected = isPlanMode && isOneTimePlan(selectedPlan);
  const isRecurringPlan = isPlanMode && Boolean(selectedPlan) && !isOneTimeSelected;
  const planIdFor = (value: PaymentProvider): string =>
    isRecurringPlan ? selectedPlan?.gatewayPlans?.[value] || '' : '';
  const availableProviders: PaymentProvider[] = isPlanMode
    ? isOneTimeSelected
      ? enabledProviders
      : enabledProviders.filter((value) => RECURRING_PROVIDERS.includes(value) && Boolean(planIdFor(value)))
    : enabledProviders;

  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>(
    enabledProviders[0],
  );
  const provider = availableProviders.includes(selectedProvider)
    ? selectedProvider
    : availableProviders[0] || enabledProviders[0];
  const planId = planIdFor(provider);
  const needsPlanChoice = isPlanMode && !selectedPlan;

  const [gatewayPlans, setGatewayPlans] = useState<Record<string, GatewayPlan[]>>({});
  const planInfo = (value: PaymentProvider, id: string) =>
    (gatewayPlans[value] || []).find((plan) => plan.id === id);
  const activePlanInfo = planInfo(provider, planId);

  // Only plans picked from existing gateway plans need their price looked up;
  // plans defined in the form carry their own.
  useEffect(() => {
    if (!isPlanMode || !crmTenantId || status === 'done' || planOptions.every(isManaged)) return;
    let cancelled = false;
    enabledProviders.forEach((value) => {
      paymentApi
        .getPlans(crmTenantId, value)
        .then((plans) => !cancelled && setGatewayPlans((cur) => ({ ...cur, [value]: plans })))
        .catch(() => undefined);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlanMode, crmTenantId, enabledProviders.join(',')]);

  const pendingKey = `formPaymentPending:${tenantId || 'unknown'}:${fieldName}`;
  const queryKey = `formPaymentQuery:${tenantId || 'unknown'}:${fieldName}`;
  const metaKey = `formPaymentMeta:${tenantId || 'unknown'}:${fieldName}`;

  const finishPayment = async (completed: CompletedPaymentDetails) => {
    sessionStorage.setItem(storageKey, '1');
    sessionStorage.setItem(metaKey, JSON.stringify(completed));
    sessionStorage.removeItem(pendingKey);
    setDialogOpen(false);
    await paymentSubmission?.completePayment(fieldName, completed);
    updateStatus('done');
  };

  useEffect(() => {
    const returnedFromRedirect =
      searchParams.get('journeyReturn') === 'payment';
    const alreadyDone = sessionStorage.getItem(storageKey) === '1';

    if (alreadyDone) {
      updateStatus('done');
      return;
    }
    if (!returnedFromRedirect) return;

    // The return URL carries only the payment markers; put the form's original
    // query params back (dropping the payment ones) once the result is handled.
    const restoreQuery = () => {
      let saved = '';
      try {
        saved = sessionStorage.getItem(queryKey) || '';
        sessionStorage.removeItem(queryKey);
      } catch {
        saved = '';
      }
      setSearchParams(new URLSearchParams(saved), { replace: true });
    };

    // Redirect-based gateways (HDFC, Cashfree subscriptions) leave the page, so
    // the order is remembered before leaving and verified with the gateway here.
    let pending: CompletedPaymentDetails | null = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
    } catch {
      pending = null;
    }
    if (!pending?.paymentId || !crmTenantId) {
      updateStatus('error', 'Could not confirm your payment. Please try again.');
      restoreQuery();
      return;
    }
    if (searchParams.get('payment_success') === 'false') {
      sessionStorage.removeItem(pendingKey);
      updateStatus('error', 'Payment failed or was cancelled.');
      restoreQuery();
      return;
    }
    updateStatus('processing');
    verifyPaid(crmTenantId, pending.paymentId, pending.provider || 'cashfree')
      .then((paid) => {
        if (paid) return finishPayment(pending!);
        sessionStorage.removeItem(pendingKey);
        updateStatus('error', 'Payment was not completed.');
      })
      .finally(restoreQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'done') return;
    // Advance/submit once per completed payment. This effect also runs when the block
    // remounts already 'done', which used to submit the form a second time.
    const advancedKey = `formPaymentAdvanced:${tenantId || 'unknown'}:${fieldName}`;
    try {
      if (sessionStorage.getItem(advancedKey) === '1') return;
      sessionStorage.setItem(advancedKey, '1');
    } catch {
      // storage unavailable: fall through and advance
    }
    if (data?.href || data?.onSubmitCallback) {
      actionButtonRef.current?.click();
      return;
    }
    if (paymentSubmission?.advanceAfterPayment) {
      paymentSubmission.advanceAfterPayment();
    } else {
      actionButtonRef.current?.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const breakdown = computePaymentBreakdown(
    Number(data?.amount) || 0,
    data?.discountMode || 'none',
    Number(data?.discountValue) || 0,
    data?.gstMode || 'none',
    gstRate,
  );
  const planBreakdown = selectedPlan && isManaged(selectedPlan) ? computePlanBreakdowns(selectedPlan, gstRate) : null;

  const startCheckout = async () => {
    if (!crmTenantId) {
      updateStatus('error', 'Missing tenant context for payment.');
      return;
    }
    if (isPlanMode && !selectedPlan) {
      updateStatus('error', 'Please choose a plan.');
      return;
    }
    if (isRecurringPlan && !planId) {
      updateStatus('error', 'No subscription plan available.');
      return;
    }

    updateStatus('processing');

    try {
      const preparePayment = data?.preparePayment || paymentSubmission?.preparePayment;
      if (!preparePayment) {
        throw new Error('Payment is unavailable in the builder preview. Save the form and open its View page to test payment.');
      }
      // Keep return_url short: the form's own query params are stashed in
      // sessionStorage and restored when the visitor comes back.
      sessionStorage.setItem(queryKey, window.location.search.replace(/^\?/, ''));
      const frontendReturn = new URL(window.location.origin + window.location.pathname);
      frontendReturn.searchParams.set('journeyReturn', 'payment');
      // Gateways that finish with a server-side POST (HDFC, Cashfree
      // subscriptions) cannot post to this static page, so they return via
      // the backend, which bounces the browser back here as a GET.
      const redirectsBack =
        REDIRECT_PROVIDERS.has(provider) ||
        (isRecurringPlan && provider === 'cashfree');
      const returnUrl = redirectsBack
        ? `${API_BASE_URL}/payment/${provider}/return?redirect=${encodeURIComponent(
            frontendReturn.toString(),
          )}`
        : frontendReturn.toString();
      const customer = await preparePayment();

      const order = await paymentApi.createFormOrder(tenantId || customer.formId, customer.formId, {
        fieldName,
        provider,
        planId: isPlanMode ? selectedPlan?.id : undefined,
        crmTenantId,
        customer: {
          leadId: customer.leadId,
          name: customer.name,
          email: customer.email,
          contact: customer.phone,
        },
        returnUrl,
        query: Object.fromEntries(new URLSearchParams(window.location.search)),
      });

      const orderData = order?.data ?? {};
      const paymentId = String(
        order?.paymentData?.id ||
          orderData.merchantOrderId ||
          orderData.hdfcOrderId ||
          order?.hdfcOrderId ||
          orderData.order_id ||
          (provider === 'razorpay' ? orderData.id : '') ||
          '',
      );
      if (!paymentId) {
        throw new Error('Failed to create payment record (payment ID missing)');
      }
      const completed: CompletedPaymentDetails = {
        fieldName,
        leadId: customer.leadId,
        paymentId,
        provider,
      };

      if (provider === 'hdfc') {
        const paymentUrl = orderData.paymentUrl || order?.paymentUrl;
        if (!paymentUrl) {
          throw new Error(order?.message || 'Failed to create HDFC payment');
        }
        sessionStorage.setItem(pendingKey, JSON.stringify(completed));
        window.location.href = String(paymentUrl);
        return;
      }

      if (provider === 'razorpay') {
        const key = String(order?.rp_key || '');
        if (!orderData.id || !key) {
          throw new Error(order?.message || 'Failed to create Razorpay order');
        }
        const outcome = await openRazorpayCheckout({
          key,
          orderId: String(orderData.id),
          isSubscription: isRecurringPlan,
          currency: 'INR',
          description: data?.description,
          prefill: { name: customer.name, email: customer.email, contact: customer.phone },
        });
        if (outcome === 'cancelled') {
          updateStatus('idle', 'Payment cancelled by user.');
          return;
        }
        if (!(await verifyPaid(crmTenantId, paymentId, provider))) {
          throw new Error('Payment could not be confirmed. If you were charged, please contact support.');
        }
        await finishPayment(completed);
        return;
      }

      // Cashfree
      const paymentSessionId =
        orderData.payment_session_id || orderData.subscription_session_id;
      const environment = order?.environment;

      if (!paymentSessionId) {
        throw new Error(
          'Failed to create payment session (session ID missing)',
        );
      }

      if (!environment || !['sandbox', 'production'].includes(environment)) {
        throw new Error(
          'Failed to determine payment environment (sandbox/production)',
        );
      }

      if (isRecurringPlan || String(paymentSessionId).startsWith('sub_session_')) {
        sessionStorage.setItem(pendingKey, JSON.stringify(completed));
        await startCashfreeSubscription(paymentSessionId, environment);
        return;
      }

      const outcome = await openCashfreeCheckout(paymentSessionId, environment);
      if (outcome === 'cancelled') {
        updateStatus('idle', 'Payment cancelled by user.');
        return;
      }
      await finishPayment(completed);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Payment failed.';
      if (/payment cancelled/i.test(String(message))) {
        updateStatus('idle', 'Payment cancelled by user.');
      } else {
        updateStatus('error', String(message));
      }
    }
  };

  const handleButtonClick = () => {
    if (!isPlanMode && data?.breakdownDisplay === 'dialog') {
      setDialogOpen(true);
      return;
    }
    startCheckout();
  };

  const hiddenActionButton = (
    <div style={{ display: 'none' }}>
      <CustomButton
        ref={actionButtonRef}
        data={{
          data: {
            type: data?.type,
            onSubmitCallback: data?.onSubmitCallback,
            href: data?.href,
            openInNewTab: data?.openInNewTab,
            submissionResponse: (data as any)?.submissionResponse,
          } as any,
          content: '',
        }}
      />
    </div>
  );

  if (status === 'done') {
    return (
      <div className={data?.className || 'btn d-flex'} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
        <span>Payment Submitted</span>
        <MuiButton
          size="small"
          variant="outlined"
          onClick={() => actionButtonRef.current?.click()}
          className={(data?.href || data?.onSubmitCallback) ? '' : 'd-none'}
        >
          Continue
        </MuiButton>
        {hiddenActionButton}
      </div>
    );
  }

  const buttonLabel =
    status === 'processing'
      ? 'Processing...'
      : typeof content === 'string'
        ? content
        : content || 'Pay Now';

  return (
    <div>
      {isPlanMode && (
        <StepHeading index={1} title="Choose your plan" done={Boolean(selectedPlan)} />
      )}
      {isPlanMode && (
        <PlanCards
          plans={planOptions}
          selectedId={selectedPlan?.id || ''}
          disabled={status === 'processing'}
          onSelect={setSelectedPlanKey}
          gstRate={gstRate}
          currency={currency}
          infoFor={(plan) => {
            const first = enabledProviders.find((value) => plan.gatewayPlans?.[value]);
            return first ? planInfo(first, plan.gatewayPlans[first]!) : undefined;
          }}
        />
      )}
      {availableProviders.length > 1 && (
        <div
          style={{
            marginBottom: 14,
            opacity: needsPlanChoice ? 0.5 : 1,
            pointerEvents: needsPlanChoice ? 'none' : 'auto',
          }}
        >
          {isPlanMode && (
            <StepHeading
              index={2}
              title="Pay with"
              hint={needsPlanChoice ? 'Select a plan first' : undefined}
            />
          )}
          <div
            role="radiogroup"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 10,
            }}
          >
            {availableProviders.map((value) => (
              <ProviderTile
                key={value}
                provider={value}
                active={provider === value}
                disabled={status === 'processing'}
                onSelect={() => setSelectedProvider(value)}
              />
            ))}
          </div>
        </div>
      )}
      {isPlanMode && planBreakdown && selectedPlan && (
        <div style={{ marginBottom: 12 }}>
          <BreakdownRows
            breakdown={planBreakdown.first}
            discountMode={selectedPlan.discountMode}
            gstMode={selectedPlan.gstMode}
            gstRate={gstRate}
            currency={currency}
          />
          {!isOneTimeSelected && selectedPlan.discountType === 'first_time' &&
            planBreakdown.first.total !== planBreakdown.recurring.total && (
              <small style={{ color: '#6b7380' }}>
                Then {formatPlanMoney(planBreakdown.recurring.total, currency)} / {formatOptionCadence(selectedPlan)}
              </small>
            )}
        </div>
      )}
      {isPlanMode && !planBreakdown && activePlanInfo && (
        <div style={{ marginBottom: 12 }}>
          <PlanDetails plan={activePlanInfo} heading={`Pay via ${PROVIDER_LABELS[provider]}`} />
        </div>
      )}
      {isPlanMode && !needsPlanChoice && availableProviders.length === 0 && (
        <div style={{ color: '#d32f2f', fontSize: '0.9rem', marginBottom: 8 }}>
          No payment provider offers this plan.
        </div>
      )}
      {!isPlanMode && (!data?.breakdownDisplay || data.breakdownDisplay === 'inline') && (
        <div style={{ marginBottom: 8 }}>
          <BreakdownRows
            breakdown={breakdown}
            discountMode={data?.discountMode}
            gstMode={data?.gstMode}
            gstRate={gstRate}
            currency={currency}
          />
        </div>
      )}
      <CustomButton
        ref={ref}
        data={{
          className: data?.className || 'btn d-flex ',
          disabled: status === 'processing' || (isRecurringPlan && !planId),
          onClick: handleButtonClick,
          data: { type: 'button' },
          content:
            status === 'processing' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                {buttonLabel}
              </span>
            ) : (
              buttonLabel
            ),
        }}
      />
      {hiddenActionButton}
      {errorMessage && (
        <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: 4 }}>
          {errorMessage}
        </div>
      )}

      {!isPlanMode && data?.breakdownDisplay === 'dialog' && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Payment Summary</DialogTitle>
          <DialogContent>
            <BreakdownRows
              breakdown={breakdown}
              discountMode={data?.discountMode}
              gstMode={data?.gstMode}
              gstRate={gstRate}
              currency={currency}
            />
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setDialogOpen(false)}>
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              disabled={status === 'processing'}
              onClick={startCheckout}
              startIcon={
                status === 'processing' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {status === 'processing'
                ? 'Processing...'
                : `Proceed to Pay ${formatCurrency(breakdown.total, currency)}`}
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
});

PaymentSection.displayName = 'PaymentSection';

export default PaymentSection;
